/**
 * API Client and Base Configuration
 *
 * Provides the axios instance with interceptors and generic HTTP methods
 * used by all domain-specific API modules.
 */

import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { config } from '../../utils/config';
import { supabase } from '../supabase';

// Create axios instance with default configuration
export const apiClient: AxiosInstance = axios.create({
  baseURL: config.apiUrl,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth token cache — updated by AuthContext via setCachedAuthToken().
// NO onAuthStateChange subscription here: each subscription triggers auth-js's
// internal _emitInitialSession() → getSession(). Having a subscription here AND
// in AuthContext creates two competing getSession() calls that deadlock on
// auth-js's Web Locks when a token refresh is in flight. See Issue #565.
let cachedToken: string | null = null;

// Resolves once AuthContext processes its first session (via setCachedAuthToken).
// The interceptor awaits this before attaching tokens to requests.
let _resolveAuthReady: () => void;
export const authReadyPromise = new Promise<void>((resolve) => {
  _resolveAuthReady = resolve;
});

// Safety timeout — if AuthContext never calls setCachedAuthToken (broken storage,
// extension interference, etc.), unblock the interceptor after 8 seconds so the
// app can at least redirect to login instead of spinning forever.
setTimeout(() => _resolveAuthReady(), 8000);

/**
 * Called by AuthContext whenever it receives a session update via its single
 * onAuthStateChange subscription. This is the ONLY way the token cache is
 * updated — no competing getSession() / onAuthStateChange calls in this module.
 */
export function setCachedAuthToken(token: string | null): void {
  cachedToken = token;
  _resolveAuthReady();
}

/**
 * Plan 254: the bearer header for a request made outside axios (the chat
 * NDJSON stream uses fetch). Same source as the interceptor below: the cache
 * AuthContext fills. NEVER call getSession() here -- see Issue #565.
 */
export async function getAuthHeader(): Promise<string | null> {
  await authReadyPromise;
  return cachedToken ? `Bearer ${cachedToken}` : null;
}

/**
 * The one 401 rule for every transport. If the cached token still matches
 * the one that was rejected, the session is truly invalid, so sign out.
 * Otherwise the auto-refresh already provided a new token and the caller can
 * retry.
 */
export function handleUnauthorized(failedAuthHeader: unknown): void {
  const currentBearer = cachedToken ? `Bearer ${cachedToken}` : null;
  if (!currentBearer || failedAuthHeader === currentBearer) {
    supabase.auth.signOut();
  }
}

// Request interceptor to add Supabase auth token
apiClient.interceptors.request.use(
  async (reqConfig) => {
    // Wait until AuthContext has processed its first session.
    await authReadyPromise;

    // Use the cached token pushed by AuthContext via setCachedAuthToken().
    // NO getSession() or onAuthStateChange in this module — see Issue #565.
    if (cachedToken) {
      reqConfig.headers.Authorization = `Bearer ${cachedToken}`;
    }

    return reqConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    if (error.response) {
      // Issue #792: the backend's global error middleware wraps every
      // HTTPException into { error: { message, details, ... } }. Mirror
      // error.message into the legacy `data.detail` string so the 64
      // string-consumer call sites across the app keep working. Never
      // mirror objects — `data.detail` must stay a string to avoid
      // breaking consumers that throw new Error(detail) or setError(detail).
      // Structured-detail consumers (uploadErrorHandler, DocumentUpload)
      // read data.error.details directly.
      const data = error.response.data as
        | { detail?: unknown; error?: { message?: unknown } }
        | undefined;
      if (
        data &&
        typeof data === 'object' &&
        data.detail === undefined &&
        data.error &&
        typeof data.error === 'object' &&
        typeof data.error.message === 'string'
      ) {
        data.detail = data.error.message;
      }

      const status = error.response.status;
      const url = error.config?.url || 'unknown';

      console.error(`[API Error] ${status} for ${url}:`, error.response.data);

      if (status === 401) {
        console.warn('[API] Unauthorized - Supabase session may be expired');
        handleUnauthorized(error.config?.headers?.Authorization);
      } else if (status === 403) {
        console.warn('[API] Forbidden - insufficient permissions');
      }
    } else if (error.request) {
      console.error('[API] Network Error - no response received');
    } else {
      console.error('[API] Request Error:', error.message);
    }
    return Promise.reject(error);
  }
);

/**
 * Base API Service Class with generic HTTP methods
 * Domain-specific API classes extend or use these methods
 */
export class BaseAPIService {
  async get<T = any>(url: string, options?: { responseType?: 'blob' | 'json', validateStatus?: (status: number) => boolean }): Promise<T> {
    const response = await apiClient.get<T>(url, options);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: { timeout?: number, headers?: Record<string, any>, onUploadProgress?: (progressEvent: any) => void }): Promise<T> {
    const response = await apiClient.post<T>(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any): Promise<T> {
    const response = await apiClient.put<T>(url, data);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: { headers?: Record<string, any> }): Promise<T> {
    const response = await apiClient.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string): Promise<T> {
    const response = await apiClient.delete<T>(url);
    return response.data;
  }
}

// Export a singleton instance of the base service for direct usage
export const baseApi = new BaseAPIService();
